import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Loader2, Plus, Trash2, Crown, CheckCircle2, ExternalLink, Package, FileUp, FileText, Download, Eye, ChevronDown, ChevronRight, AlertTriangle, Plane, Ship, MapPin, Pencil, Calendar, Star, Send, Search, ChevronRight as ChevronRightIcon, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PaginationBar, paginate } from "@/components/Pagination";
import { MobileCard } from "@/components/MobileCard";

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
const fmtBytes = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const fmtDate = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; } };

const STATUS_META = {
  awaiting_input: { label: "Awaiting Freight Input", cls: "bg-[#E6ECF5] text-[#1B4383]" },
  quoted: { label: "Quotes In — Awaiting Selection", cls: "bg-[#F6EDD9] text-[#A87F31]" },
  selected: { label: "Freight Selected", cls: "bg-[#E4EEE8] text-[#527D63]" },
  completed: { label: "Completed", cls: "bg-[#E6E9EE] text-[#48566B]" },
};
const money = (c, a) => `${c || "USD"} ${Number(a).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDay = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const fmtTransit = (q) => {
  const w = q.lead_weeks;
  if (w == null || w === "") return q.lead_time || "—";
  const n = Number(w);
  const days = Math.round(n * 7);
  if (q.lead_unit === "days") return `${days} day${days === 1 ? "" : "s"}`;
  if (Number.isInteger(n)) return `${n} wk${n === 1 ? "" : "s"}`;
  return `${days} day${days === 1 ? "" : "s"}`;
};
const INPUT_YELLOW = "bg-[#FFFBEB] border-[#EAD9A0] focus-visible:ring-[#E0B84B]";
const fmtThousands = (v) => {
  if (v === "" || v == null) return "";
  const [i, d] = String(v).split(".");
  const ii = i.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return d != null ? `${ii}.${d}` : ii;
};

function QuoteForm({ reqId, onAdded, mode, methods = [], compact = false }) {
  const multi = mode !== "delivery" && methods.length > 1;
  const [qMethod, setQMethod] = useState("");
  const [forwarders, setForwarders] = useState([]);
  const [addMode, setAddMode] = useState("existing");
  const [f, setF] = useState({ forwarder_id: "", forwarder_name: "", amount: "", currency: mode === "delivery" ? "TZS" : "USD", lead_time: "", lead_weeks: "", notes: "" });
  const [transitUnit, setTransitUnit] = useState("weeks");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadFwd = useCallback(() => api.get("/freight-forwarders").then((r) => setForwarders(r.data)).catch(() => {}), []);
  useEffect(() => { loadFwd(); }, [loadFwd]);

  const submit = async () => {
    if (addMode === "new" && !f.forwarder_name.trim()) { toast.error("Enter the new forwarder's name."); return; }
    if (addMode === "existing" && !f.forwarder_id) { toast.error("Select a forwarder."); return; }
    if (f.amount === "") { toast.error("Enter the quote amount."); return; }
    if (multi && !qMethod) { toast.error("Pick the mode this rate is for (Air, Road or Sea)."); return; }
    setBusy(true);
    try {
      const transitWeeks = f.lead_weeks === "" ? null : (transitUnit === "days" ? Number(f.lead_weeks) / 7 : Number(f.lead_weeks));
      const payload = { amount: Number(f.amount), currency: f.currency || "USD", lead_time: f.lead_time, lead_weeks: transitWeeks, lead_unit: transitUnit, notes: f.notes, method: multi ? qMethod : (methods[0] || "") };
      if (addMode === "new") payload.forwarder_name = f.forwarder_name.trim();
      else payload.forwarder_id = f.forwarder_id;
      if (file) { payload.file_base64 = await readFileBase64(file); payload.filename = file.name; payload.content_type = file.type; }
      await api.post(`/freight-requests/${reqId}/quotes`, payload);
      setF({ forwarder_id: "", forwarder_name: "", amount: "", currency: f.currency, lead_time: "", lead_weeks: "", notes: "" });
      setFile(null); setAddMode("existing"); loadFwd(); onAdded();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border bg-slate-50 p-2" data-testid={`quote-form-${reqId}`}>
        <div className="w-full min-w-[160px] sm:w-auto sm:flex-1">
          {addMode === "existing" ? (
            <Select value={f.forwarder_id || ""} onValueChange={(v) => { if (v === "__new__") setAddMode("new"); else setF({ ...f, forwarder_id: v }); }}>
              <SelectTrigger className="h-9 bg-white text-xs" data-testid={`qf-forwarder-select-${reqId}`}><SelectValue placeholder="Forwarder" /></SelectTrigger>
              <SelectContent>
                {forwarders.map((fw) => <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>)}
                <SelectItem value="__new__" className="text-[#1B4383]">＋ New forwarder</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input value={f.forwarder_name} onChange={(e) => setF({ ...f, forwarder_name: e.target.value })} onBlur={() => !f.forwarder_name.trim() && setAddMode("existing")} placeholder="New forwarder name" className="h-9 bg-white" data-testid={`qf-new-name-${reqId}`} autoFocus />
          )}
        </div>
        <Input inputMode="decimal" value={fmtThousands(f.amount)} onChange={(e) => setF({ ...f, amount: e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "") })} placeholder={f.currency} className="h-9 min-w-[110px] flex-1 bg-white sm:w-28 sm:flex-none" data-testid={`qf-amount-${reqId}`} />
        <div className="flex h-9 overflow-hidden rounded-md border border-input bg-white">
          <Input type="number" value={f.lead_weeks} onChange={(e) => setF({ ...f, lead_weeks: e.target.value })} placeholder={transitUnit === "days" ? "Days" : "Wks"} className="h-full w-24 rounded-none border-0" data-testid={`qf-lead-weeks-${reqId}`} />
          {[["weeks", "Wks"], ["days", "Days"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setTransitUnit(v)} className={`px-2.5 text-[11px] font-semibold transition-colors ${transitUnit === v ? "bg-[#1B4383] text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`qf-transit-unit-${v}-${reqId}`}>{l}</button>
          ))}
        </div>
        <label title={file ? file.name : "Attach quote"} className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border bg-white ${file ? "border-[#527D63] text-[#527D63]" : "border-input text-slate-400 hover:text-slate-700"}`} data-testid={`qf-file-label-${reqId}`}>
          <FileUp className="h-4 w-4" /><input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`qf-file-${reqId}`} />
        </label>
        <Button size="sm" onClick={submit} disabled={busy} className="h-9 bg-[#1B4383] hover:bg-[#16386e]" data-testid={`qf-add-${reqId}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}<span className="ml-1">Add</span></Button>
        <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder="Note (optional) — e.g. valid 14 days, excludes duties" className="h-9 w-full bg-white text-xs" data-testid={`qf-note-${reqId}`} />
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-dashed border-border bg-[#FAFBFD] p-3" data-testid={`quote-form-${reqId}`}>
      <div className="mb-2 text-[11px] font-semibold text-slate-500">Add a Forwarder Quote</div>
      {multi && (
        <div className="mb-2 flex flex-wrap items-center gap-2" data-testid={`qf-mode-${reqId}`}>
          <span className="text-[11px] font-medium text-slate-600">This Rate Is For</span>
          {methods.map((m) => (
            <button key={m} type="button" onClick={() => setQMethod(m)} className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase transition-colors ${qMethod === m ? "border-[#12294B] bg-[#12294B] text-white" : "border-[#EAD9A0] bg-[#FFFBEB] text-[#8A6D1F] hover:bg-amber-50"}`} data-testid={`qf-mode-${m}-${reqId}`}>{m}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
        <div className="sm:col-span-4">
          <Label className="text-[11px]">{mode === "delivery" ? "Delivery Partner" : "Freight Forwarder"}</Label>
          {addMode === "existing" ? (
            <Select value={f.forwarder_id || ""} onValueChange={(v) => { if (v === "__new__") { setAddMode("new"); } else { setF({ ...f, forwarder_id: v }); } }}>
              <SelectTrigger className={`h-9 text-xs ${!f.forwarder_id ? INPUT_YELLOW : ""}`} data-testid={`qf-forwarder-select-${reqId}`}><SelectValue placeholder="Select Forwarder" /></SelectTrigger>
              <SelectContent>
                {forwarders.map((fw) => <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>)}
                <SelectItem value="__new__" className="text-[#1B4383]">＋ Add new forwarder</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-1.5">
              <Input value={f.forwarder_name} onChange={(e) => setF({ ...f, forwarder_name: e.target.value })} placeholder="New Forwarder Name" className={`h-9 ${!f.forwarder_name.trim() ? INPUT_YELLOW : ""}`} data-testid={`qf-new-name-${reqId}`} autoFocus />
              <button type="button" onClick={() => { setAddMode("existing"); setF({ ...f, forwarder_name: "" }); }} className="whitespace-nowrap text-[11px] text-[#1B4383] hover:underline" data-testid={`qf-use-existing-${reqId}`}>Pick existing</button>
            </div>
          )}
        </div>
        <div className="sm:col-span-2"><Label className="text-[11px]">Currency</Label><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className={`h-9 ${INPUT_YELLOW}`} data-testid={`qf-curr-${reqId}`} /></div>
        <div className="sm:col-span-2"><Label className="text-[11px]">Amount</Label><Input inputMode="decimal" value={fmtThousands(f.amount)} onChange={(e) => setF({ ...f, amount: e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "") })} className={`h-9 ${f.amount === "" ? INPUT_YELLOW : ""}`} data-testid={`qf-amount-${reqId}`} /></div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between gap-1">
            <Label className="text-[11px]">Transit Time</Label>
            <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid={`qf-transit-unit-${reqId}`}>
              {[["weeks", "Wks"], ["days", "Days"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setTransitUnit(v)}
                  className={`px-2.5 py-1 text-xs font-semibold transition-colors ${transitUnit === v ? "bg-[#12294B] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                  data-testid={`qf-transit-unit-${v}-${reqId}`}>{l}</button>
              ))}
            </div>
          </div>
          <Input type="number" value={f.lead_weeks} onChange={(e) => setF({ ...f, lead_weeks: e.target.value })} placeholder={transitUnit === "days" ? "e.g. 7" : "e.g. 1"} className={`h-9 ${f.lead_weeks === "" ? INPUT_YELLOW : ""}`} data-testid={`qf-lead-weeks-${reqId}`} />
        </div>
        <div className="sm:col-span-2"><Label className="text-[11px]">Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional" className="h-9" data-testid={`qf-notes-${reqId}`} /></div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50 ${file ? "border-input bg-white text-slate-700" : "border-[#EAD9A0] bg-[#FFFBEB] text-[#8A6D1F]"}`} data-testid={`qf-file-label-${reqId}`}>
          <FileUp className="h-3.5 w-3.5" /> {file ? file.name : "Attach Forwarder's Quote (Optional)"}
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`qf-file-${reqId}`} />
        </label>
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`qf-add-${reqId}`}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Add Quote</Button>
      </div>
    </div>
  );
}

function FreightFiles({ rfqId, supplierId, canQuote }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => {
    api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/quote-files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [rfqId, supplierId]);
  useEffect(() => { load(); }, [load]);
  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const b64 = await readFileBase64(f);
        await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/quote-files`, { filename: f.name, content_type: f.type, file_base64: b64 });
      }
      load();
      toast.success(list.length > 1 ? `${list.length} freight quotes saved to RFQ files` : "Freight quote saved to RFQ files");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };
  const download = async (f) => {
    try {
      const res = await api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/quote-files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = f.filename || "quote";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="mt-3 rounded-md border border-dashed border-border bg-[#FAFBFD] p-3" data-testid="freight-files">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-slate-500">Freight Quote Documents</span>
        {canQuote && (
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="freight-file-upload-label">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Upload Received Quote
            <input type="file" multiple className="hidden" onChange={upload} data-testid="freight-file-input" />
          </label>
        )}
      </div>
      {files.length ? (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs" data-testid={`freight-file-${f.id}`}>
              <FileText className="h-3.5 w-3.5 shrink-0 text-[#1B4383]" />
              <span className="min-w-0 flex-1 truncate text-slate-700">{f.filename}</span>
              <span className="shrink-0 text-[10px] text-muted-foreground">{fmtBytes(f.size)}</span>
              <button onClick={() => download(f)} className="shrink-0 text-slate-400 hover:text-primary" data-testid={`freight-file-dl-${f.id}`}><Download className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">No documents yet. Upload the forwarder quotes you received — they're saved to this RFQ's Quote Documents.</p>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function RequestRow({ r, active, onClick }) {
  const meta = STATUS_META[r.status] || STATUS_META.awaiting_input;
  const quotes = r.forwarder_quotes || [];
  const cheapest = quotes.length ? Math.min(...quotes.map((q) => q.amount)) : null;
  const cheapestQ = cheapest != null ? quotes.find((q) => q.amount === cheapest) : null;
  const dot = r.status === "completed" ? "bg-slate-300" : r.status === "awaiting_input" ? "bg-slate-400" : "bg-[#1B4383]";
  return (
    <button type="button" onClick={onClick} data-testid={`freight-row-${r.id}`}
      className={`w-full rounded-lg border px-3.5 py-3 text-left transition-all ${active ? "border-[#1B4383] bg-[#F5F8FD] ring-1 ring-[#1B4383]/20 shadow-sm" : "border-border bg-white hover:border-[#1B4383]/30 hover:bg-slate-50"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
          <span className="truncate font-heading text-sm font-bold text-slate-900">{r.supplier_name}</span>
        </div>
        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{r.rfq_code}</span>
      </div>
      <div className="mt-1 truncate text-xs font-medium text-slate-700">{r.client_name || "—"}{r.rfq_title ? ` · ${r.rfq_title}` : ""}</div>
      {r.created_at && <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`freight-row-requested-${r.id}`}>Requested {fmtDay(r.created_at)}</div>}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-medium text-slate-500">{isMulti(r) ? r.methods.map((m) => `${MODE_NAME[m]} ${modeState(r, m).qs.length}`).join(" · ") : `${quotes.length}/3 quotes`}</span>
          {cheapestQ && <span className="font-mono text-slate-600">{money(cheapestQ.currency, cheapest)}</span>}
        </div>
      </div>
    </button>
  );
}

const MODE_NAME = { air: "Air", road: "Road", sea: "Sea" };
const isMulti = (r) => r.mode !== "delivery" && (r.methods || []).length > 1;
const modeState = (r, m) => {
  const qs = m ? (r.forwarder_quotes || []).filter((q) => q.method === m) : (r.forwarder_quotes || []);
  const lc = m ? (r.local_costs_by_mode || {})[m] : r.local_costs;
  return { qs, lc, cheapest: qs.length ? qs.reduce((a, b) => (b.amount < a.amount ? b : a)) : null, done: qs.length > 0 && !!lc };
};

function ModeTabs({ r, active, setActive }) {
  return (
    <div className={`grid gap-2 ${r.methods.length === 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid={`freight-mode-tabs-${r.id}`}>
      {r.methods.map((m) => {
        const Icon = MODE_ICON[m] || Truck;
        const s = modeState(r, m);
        const on = active === m;
        return (
          <button key={m} type="button" onClick={() => setActive(m)} data-testid={`freight-mode-tab-${m}-${r.id}`}
            className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-[#1B4383] bg-[#F5F8FD] ring-1 ring-[#1B4383]/20" : "border-border bg-white hover:bg-slate-50"}`}>
            <div className="flex items-center justify-between gap-1">
              <span className={`inline-flex items-center gap-1.5 font-heading text-sm font-bold ${on ? "text-[#1B4383]" : "text-slate-900"}`}><Icon className="h-4 w-4" />{MODE_NAME[m]}</span>
              {s.done ? <CheckCircle2 className="h-4 w-4 text-[#1B4383]" /> : <span className="h-2 w-2 rounded-full bg-slate-300" />}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">{s.qs.length}/3 Rates · {s.lc ? "Costs ✓" : "Costs Needed"}</div>
            {s.cheapest && <div className="mt-0.5 truncate font-mono text-xs text-slate-700">From {money(s.cheapest.currency, s.cheapest.amount)}</div>}
          </button>
        );
      })}
    </div>
  );
}

function LocalCostsCard({ r, canQuote, onChanged, onSaved, method, compact = false }) {
  const lc = method ? (r.local_costs_by_mode || {})[method] : r.local_costs;
  const [f, setF] = useState({ transport_total: lc?.transport_total ?? "", misc_total: lc?.misc_total ?? "", notes: lc?.notes || "" });
  const [busy, setBusy] = useState(false);
  const cur = r.mode === "delivery" ? "TZS" : "USD";
  const save = async () => {    if (f.transport_total === "" || f.misc_total === "") { toast.error("Enter both figures (use 0 if none)."); return; }
    setBusy(true);
    try {
      await api.put(`/freight-requests/${r.id}/local-costs`, { transport_total: Number(f.transport_total), misc_total: Number(f.misc_total), notes: f.notes, method: method || "" });
      toast.success(method ? `${MODE_NAME[method]} transport & misc saved` : "Transport & Misc saved — fed into costing");
      onChanged();
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const num = (k) => (e) => setF({ ...f, [k]: e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "") });
  if (method || compact) {
    return (
      <div className="flex flex-wrap items-end gap-2" data-testid={`freight-local-costs-${r.id}-${method || "single"}`}>
        <div className="w-36"><Label className="text-[11px]">Transport &amp; Labour</Label><Input inputMode="decimal" disabled={!canQuote} value={fmtThousands(f.transport_total)} onChange={num("transport_total")} placeholder={cur} className="h-9" data-testid={`lc-transport-${r.id}`} /></div>
        <div className="w-28"><Label className="text-[11px]">Misc</Label><Input inputMode="decimal" disabled={!canQuote} value={fmtThousands(f.misc_total)} onChange={num("misc_total")} placeholder={cur} className="h-9" data-testid={`lc-misc-${r.id}`} /></div>
        {canQuote && (() => { const clean = lc && Number(f.transport_total) === Number(lc.transport_total) && Number(f.misc_total) === Number(lc.misc_total); return <Button size="sm" variant={clean ? "outline" : "default"} onClick={save} disabled={busy} className={`h-9 ${clean ? "" : "bg-[#1B4383] hover:bg-[#16386e]"}`} data-testid={`lc-save-${r.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : clean ? <CheckCircle2 className="mr-1 h-4 w-4 text-[#1B4383]" /> : null}{clean ? "Saved" : "Save"}</Button>; })()}
      </div>
    );
  }
  return (
    <div id={`freight-local-costs-${r.id}`} className="h-fit max-w-xl rounded-lg border border-border bg-white px-4 py-3" data-testid={`freight-local-costs-${r.id}${method ? `-${method}` : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-heading text-sm font-bold text-slate-900">{method ? `${MODE_NAME[method]} · ` : ""}Transport, Labour &amp; Misc</div>
        {lc ? <span className="rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]" data-testid={`lc-saved-${r.id}`}>Saved by {lc.by_name}</span> : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Required Before Hand Off</span>}
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{method ? `In-country transport, offloading labour and any other costs if this goes by ${MODE_NAME[method].toLowerCase()}, in ${cur}. Used in costing when a ${MODE_NAME[method].toLowerCase()} rate wins.` : `In-country transport, offloading labour and any other costs, in ${cur}. These go straight into the costing.`}</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div><Label className="text-[11px]">Transport &amp; Labour ({cur})</Label><Input inputMode="decimal" disabled={!canQuote} value={fmtThousands(f.transport_total)} onChange={num("transport_total")} className={`h-9 ${f.transport_total === "" ? INPUT_YELLOW : ""}`} data-testid={`lc-transport-${r.id}`} /></div>
        <div><Label className="text-[11px]">Misc ({cur})</Label><Input inputMode="decimal" disabled={!canQuote} value={fmtThousands(f.misc_total)} onChange={num("misc_total")} className={`h-9 ${f.misc_total === "" ? INPUT_YELLOW : ""}`} data-testid={`lc-misc-${r.id}`} /></div>
        <div className="col-span-2"><Label className="text-[11px]">Notes</Label><Input disabled={!canQuote} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. 2 trucks + crane offload" className="h-9" data-testid={`lc-notes-${r.id}`} /></div>
        {canQuote && <Button size="sm" className="col-span-2 bg-[#1B4383] hover:bg-[#16386e]" onClick={save} disabled={busy} data-testid={`lc-save-${r.id}`}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}{method ? `Save ${MODE_NAME[method]} Costs` : "Save & Continue"}</Button>}
      </div>
    </div>
  );
}

const FREIGHT_STEPS = [
  { n: 1, short: "Quotes", label: "Forwarder Quotes", desc: "Add The Forwarder Quotes — Aim For 3 To Compare Fairly." },
  { n: 2, short: "Costs", label: "Transport & Misc", desc: "Enter In-Country Transport, Labour And Any Other Costs (Use 0 If None)." },
  { n: 3, short: "Send", label: "Review & Send", desc: "Star Your Suggested Forwarder, Then Send Everything Back To Operations." },
];

function FreightStepper({ r, view, setView, done }) {
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid={`freight-stepper-${r.id}`}>
      {FREIGHT_STEPS.map((s) => {
        const active = view === s.n;
        const ok = done[s.n];
        return (
          <button key={s.n} type="button" onClick={() => setView(s.n)} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${active ? "border-[#1B4383] bg-[#F5F8FD]" : "border-border bg-white hover:bg-slate-50"}`} data-testid={`freight-step-${s.n}-${r.id}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${active ? "bg-[#1B4383] text-white" : ok ? "bg-[#527D63] text-white" : "bg-slate-100 text-slate-500"}`}>{ok && !active ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.n}</span>
            <span className="min-w-0">
              <span className={`block truncate text-xs font-semibold ${active ? "text-[#1B4383]" : "text-slate-700"}`}><span className="sm:hidden">{s.short}</span><span className="hidden sm:inline">{s.label}</span></span>
              <span className="hidden truncate text-[10px] text-muted-foreground sm:block">{ok || ""}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StepNav({ r, onBack, onNext, nextLabel, nextDisabled, hint }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
      {onBack ? <Button size="sm" variant="outline" onClick={onBack} data-testid={`freight-back-${r.id}`}>Back</Button> : <span />}
      <div className="flex items-center gap-2">
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
        {onNext && <Button size="sm" onClick={onNext} disabled={nextDisabled} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid={`freight-next-${r.id}`}>{nextLabel}<ChevronRightIcon className="ml-1 h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function RateRow({ r, q, canQuote, canSelect, busy, onChanged, onRemove, onSuggest, onView, onSelect }) {
  const star = r.suggested_quote_id === q.id;
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const start = () => {
    const w = q.lead_weeks;
    const unit = q.lead_unit === "days" || (q.lead_unit !== "weeks" && w != null && w !== "" && !Number.isInteger(Number(w))) ? "days" : "weeks";
    setEdit({ amount: String(q.amount ?? ""), unit, notes: q.notes || "", lead: w == null || w === "" ? "" : String(unit === "days" ? Math.round(Number(w) * 7) : Math.round(Number(w) * 10) / 10) });
  };
  const save = async () => {
    if (edit.amount === "") { toast.error("Enter the amount."); return; }
    setSaving(true);
    try {
      const lw = edit.lead === "" ? null : (edit.unit === "days" ? Number(edit.lead) / 7 : Number(edit.lead));
      await api.put(`/freight-requests/${r.id}/quotes/${q.id}`, { amount: Number(edit.amount), lead_weeks: lw, lead_unit: edit.unit, notes: edit.notes });
      toast.success("Rate updated"); setEdit(null); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-2.5 py-1.5 text-sm ${edit ? "border-[#1B4383] bg-[#F5F8FD]" : "border-border"}`} data-testid={`freight-quote-${q.id}`}>
      {canQuote && !edit && <button onClick={() => onSuggest(q.id)} disabled={busy === q.id} title="Suggest this one" data-testid={`suggest-quote-${q.id}`}><Star className={`h-4 w-4 ${star ? "fill-[#1B4383] text-[#1B4383]" : "text-slate-300 hover:text-[#1B4383]"}`} /></button>}
      <span className="min-w-[120px] flex-1 truncate font-medium text-slate-800">{q.forwarder_name}</span>
      {edit ? (<>
        <Input inputMode="decimal" autoFocus value={fmtThousands(edit.amount)} onChange={(e) => setEdit({ ...edit, amount: e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "") })} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(null); }} placeholder={q.currency || "USD"} className="h-8 w-28 bg-white" data-testid={`rate-edit-amount-${q.id}`} />
        <div className="flex h-8 overflow-hidden rounded-md border border-input bg-white">
          <Input type="number" value={edit.lead} onChange={(e) => setEdit({ ...edit, lead: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") save(); }} className="h-full w-20 rounded-none border-0" data-testid={`rate-edit-transit-${q.id}`} />
          {[["weeks", "Wks"], ["days", "Days"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setEdit({ ...edit, unit: v })} className={`px-2 text-[11px] font-semibold ${edit.unit === v ? "bg-[#1B4383] text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`rate-edit-unit-${v}-${q.id}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" className="h-8 bg-[#1B4383] hover:bg-[#16386e]" onClick={save} disabled={saving} data-testid={`rate-edit-save-${q.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        <button onClick={() => setEdit(null)} className="text-xs text-muted-foreground hover:text-slate-800" data-testid={`rate-edit-cancel-${q.id}`}>Cancel</button>
        <Input value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEdit(null); }} placeholder="Note (optional)" className="h-8 w-full bg-white text-xs" data-testid={`rate-edit-note-${q.id}`} />
      </>) : (<>
        <span className="whitespace-nowrap font-mono text-slate-800">{money(q.currency, q.amount)}</span>
        <span className="w-16 text-right text-xs text-muted-foreground">{fmtTransit(q)}</span>
        {canSelect && <Button size="sm" variant={r.selected_quote_id === q.id ? "default" : "outline"} className="h-7 px-2 text-xs" onClick={() => onSelect(q.id)} disabled={busy === q.id} data-testid={`select-quote-${q.id}`}>{r.selected_quote_id === q.id ? "Winner" : "Select"}</Button>}
        {q.file_id && <button onClick={() => onView(q.file_id)} className="text-[#1B4383]" data-testid={`view-quote-${q.id}`}><Eye className="h-4 w-4" /></button>}
        {canQuote && <button onClick={start} title="Edit rate" className="text-slate-300 hover:text-[#1B4383]" data-testid={`edit-quote-${q.id}`}><Pencil className="h-4 w-4" /></button>}
        {canQuote && <button onClick={() => onRemove(q.id)} className="text-slate-300 hover:text-destructive" data-testid={`remove-quote-${q.id}`}><Trash2 className="h-4 w-4" /></button>}
        {q.notes && <div className="w-full whitespace-pre-wrap break-words pl-6 text-xs text-slate-500" data-testid={`rate-note-${q.id}`}>{q.notes}</div>}
      </>)}
    </div>
  );
}

function ModePanel({ r, m, canQuote, canSelect, busy, onChanged, onSaved, onRemove, onSuggest, onView, onSelect }) {
  const s = modeState(r, m);
  return (
    <div className="space-y-4 rounded-xl border border-border p-3 sm:p-4" data-testid={`mode-panel-${m}-${r.id}`}>
      <div className="space-y-1.5">
        <div className="text-xs font-semibold text-slate-500">Rates <span className="text-slate-400">{s.qs.length}/3</span></div>
        {s.qs.map((q) => <RateRow key={q.id} r={r} q={q} canQuote={canQuote} canSelect={canSelect} busy={busy} onChanged={onChanged} onRemove={onRemove} onSuggest={onSuggest} onView={onView} onSelect={onSelect} />)}
        {canQuote && s.qs.length < 3 && <QuoteForm compact key={`${m}-${s.qs.length}`} reqId={r.id} onAdded={onChanged} mode={r.mode} methods={m ? [m] : r.methods || []} />}
      </div>
      <LocalCostsCard key={`${m}-${s.lc?.at || ""}`} r={r} method={m} compact canQuote={canQuote} onChanged={onChanged} onSaved={onSaved} />
    </div>
  );
}

function MultiSend({ r, canQuote, busy, onHandoff }) {
  const ready = isMulti(r) ? r.methods.every((m) => modeState(r, m).done) : modeState(r, null).done;
  if (!canQuote) return null;
  if (r.handed_to_ops) return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-[#F5F8FD] px-3 py-2.5">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B4383]" data-testid={`freight-sent-chip-${r.id}`}><CheckCircle2 className="h-4 w-4" />Sent To Operations</span>
      <button onClick={onHandoff} disabled={busy === "handoff"} className="text-[11px] text-[#1B4383] hover:underline" data-testid={`freight-resend-${r.id}`}>Send Again</button>
    </div>
  );
  return (
    <Button onClick={onHandoff} disabled={busy === "handoff" || !ready} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid={`freight-handoff-btn-${r.id}`}>
      {busy === "handoff" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}{ready ? "Send Back To Operations" : isMulti(r) ? "Fill Every Mode To Send" : "Add A Rate And Costs To Send"}
    </Button>
  );
}

function SuggestButton({ r, q, canQuote, busy, onSuggest }) {
  const on = r.suggested_quote_id === q.id;
  return (
    <button type="button" disabled={!canQuote || busy === q.id} onClick={() => onSuggest(q.id)} className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${on ? "border-[#A87F31] bg-[#FBF6E9]" : "border-border bg-white hover:bg-slate-50"}`} data-testid={`review-suggest-${q.id}`}>
      <span className="flex items-center gap-2 font-medium text-slate-800"><Star className={`h-4 w-4 ${on ? "fill-[#A87F31] text-[#A87F31]" : "text-slate-300"}`} />{q.forwarder_name}</span>
      <span className="font-mono text-slate-700">{money(q.currency, q.amount)} · {fmtTransit(q)}</span>
    </button>
  );
}

function MultiReview({ r, canQuote, busy, onSuggest }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid={`freight-review-modes-${r.id}`}>
      {r.methods.map((m) => {
        const Icon = MODE_ICON[m] || Truck;
        const s = modeState(r, m);
        return (
          <div key={m} className="space-y-1.5 rounded-lg border border-border bg-slate-50/60 p-3" data-testid={`review-mode-${m}-${r.id}`}>
            <div className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-900"><Icon className="h-4 w-4 text-[#1B4383]" />{MODE_NAME[m]}</div>
            {s.qs.length ? s.qs.map((q) => <SuggestButton key={q.id} r={r} q={q} canQuote={canQuote} busy={busy} onSuggest={onSuggest} />) : <div className="text-[11px] text-[#A87F31]">No Rates Yet</div>}
            <div className="text-[11px] text-slate-600">{s.lc ? <>Transport &amp; Labour <b className="font-mono">{money("USD", s.lc.transport_total)}</b> · Misc <b className="font-mono">{money("USD", s.lc.misc_total)}</b></> : <span className="text-[#A87F31]">Transport &amp; Misc Needed</span>}</div>
          </div>
        );
      })}
    </div>
  );
}

function ReviewStep({ r, quotes, canQuote, busy, onSuggest, onHandoff }) {
  const multi = isMulti(r);
  const lc = r.local_costs;
  const ready = multi ? r.methods.every((m) => modeState(r, m).done) : quotes.length && lc;
  const cur = r.mode === "delivery" ? "TZS" : "USD";
  return (
    <div className={`${multi ? "" : "max-w-xl"} space-y-3`} data-testid={`freight-review-${r.id}`}>
      <div className="text-xs font-semibold text-slate-600">Your Suggested Forwarder {canQuote && <span className="font-normal text-muted-foreground">— Tap One To Star It</span>}</div>
      {multi && <MultiReview r={r} canQuote={canQuote} busy={busy} onSuggest={onSuggest} />}
      {!multi && <div className="space-y-1.5">
        {quotes.map((q) => {
          const on = r.suggested_quote_id === q.id;
          return (
            <button key={q.id} type="button" disabled={!canQuote || busy === q.id} onClick={() => onSuggest(q.id)} className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${on ? "border-[#A87F31] bg-[#FBF6E9]" : "border-border bg-white hover:bg-slate-50"}`} data-testid={`review-suggest-${q.id}`}>
              <span className="flex items-center gap-2 font-medium text-slate-800"><Star className={`h-4 w-4 ${on ? "fill-[#A87F31] text-[#A87F31]" : "text-slate-300"}`} />{q.forwarder_name}{q.method && ((r.methods || []).length > 1 || !(r.methods || [r.method]).includes(q.method)) && <span className="rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#1B4383]">{q.method}</span>}</span>
              <span className="font-mono text-slate-700">{money(q.currency, q.amount)} · {fmtTransit(q)}</span>
            </button>
          );
        })}
      </div>}
      {!multi && <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid={`review-costs-${r.id}`}>
        {lc ? <>Transport &amp; Labour <b className="font-mono">{money(cur, lc.transport_total)}</b> · Misc <b className="font-mono">{money(cur, lc.misc_total)}</b>{lc.notes ? ` · ${lc.notes}` : ""}</> : <span className="text-[#A87F31]">Transport &amp; Misc Not Entered Yet — Go Back To Step 2.</span>}
      </div>}
      {multi && !ready && <p className="text-[11px] text-[#A87F31]" data-testid={`review-missing-${r.id}`}>Add at least one rate and the transport &amp; misc costs for every mode before sending.</p>}
      {canQuote && (r.handed_to_ops ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#BFD6C6] bg-[#F3F8F4] px-3 py-2.5">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3F6B50]" data-testid={`freight-sent-chip-${r.id}`}><CheckCircle2 className="h-4 w-4" />Sent To Operations{r.handed_to_ops_at ? ` · ${new Date(r.handed_to_ops_at).toLocaleString()}` : ""}</span>
          <button onClick={onHandoff} disabled={busy === "handoff"} className="text-[11px] text-[#1B4383] hover:underline" data-testid={`freight-resend-${r.id}`}>Send Again</button>
        </div>
      ) : (
        <Button onClick={onHandoff} disabled={busy === "handoff" || !ready} className="w-full bg-[#1B4383] hover:bg-[#16386e]" data-testid={`freight-handoff-btn-${r.id}`}>
          {busy === "handoff" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Send Back To Operations
        </Button>
      ))}
    </div>
  );
}

function RequestDetail({ r, canQuote, canSelect, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState("");
  const [editId, setEditId] = useState(null);
  const [editF, setEditF] = useState({});
  const [editUnit, setEditUnit] = useState("weeks");
  const [actFilter, setActFilter] = useState({ action: "all", from: "", to: "" });
  const ACT_LABEL = { created: "Created", quote_added: "Quote added", quote_removed: "Quote removed", quote_edited: "Quote edited", winner_selected: "Winner selected", suggested: "Suggested", completed: "Completed / Reopened", handoff: "Handoff to Ops", doc_upload: "Document", local_costs: "Transport & Misc" };
  const allActs = [...(r.activity || [])].reverse();
  const actOptions = [...new Set(allActs.map((a) => a.action).filter(Boolean))];
  const shownActs = allActs.filter((a) => {
    if (actFilter.action !== "all" && a.action !== actFilter.action) return false;
    const d = (a.at || "").slice(0, 10);
    if (actFilter.from && (!d || d < actFilter.from)) return false;
    if (actFilter.to && (!d || d > actFilter.to)) return false;
    return true;
  });
  const actDirty = actFilter.action !== "all" || actFilter.from || actFilter.to;
  const meta = STATUS_META[r.status] || STATUS_META.awaiting_input;
  const quotes = r.forwarder_quotes || [];
  const multi = isMulti(r);
  const compact = true;
  const [activeMode, setActiveMode] = useState(() => (multi ? (r.methods.find((m) => !modeState(r, m).done) || r.methods[0]) : ""));
  const shownQuotes = multi ? quotes.filter((q) => q.method === activeMode) : quotes;
  const nextMode = () => { const i = r.methods.indexOf(activeMode); const nx = [...r.methods.slice(i + 1), ...r.methods.slice(0, i)].find((m) => !modeState(r, m).done); if (nx) setActiveMode(nx); };
  const startEdit = (q) => {
    setEditId(q.id);
    setEditUnit("weeks");
    setEditF({ currency: q.currency || "USD", amount: q.amount ?? "", lead_weeks: q.lead_weeks ?? "", lead_time: q.lead_time || "", notes: q.notes || "" });
  };
  const saveEdit = async (qid) => {
    if (editF.amount === "" || editF.amount == null) { toast.error("Enter the quote amount."); return; }
    setBusy(qid);
    try {
      const transitWeeks = editF.lead_weeks === "" || editF.lead_weeks == null ? null : (editUnit === "days" ? Number(editF.lead_weeks) / 7 : Number(editF.lead_weeks));
      await api.put(`/freight-requests/${r.id}/quotes/${qid}`, { amount: Number(editF.amount), currency: editF.currency || "USD", lead_weeks: transitWeeks, lead_unit: editUnit, lead_time: editF.lead_time, notes: editF.notes });
      toast.success("Quote updated");
      setEditId(null); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const select = async (qid) => {
    setBusy(qid);
    try { await api.post(`/freight-requests/${r.id}/select`, { quote_id: qid }); toast.success("Winner selected — freight fed into costing"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const remove = async (qid) => {
    setBusy(qid);
    try { await api.delete(`/freight-requests/${r.id}/quotes/${qid}`); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const viewFile = async (fileId) => {
    try {
      const res = await api.get(`/rfqs/${r.rfq_id}/suppliers/${r.supplier_id}/quote-files/${fileId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the quote"); }
  };
  const cheapest = quotes.length ? Math.min(...quotes.map((q) => q.amount)) : null;
  const complete = async () => {
    setBusy("complete");
    try {
      const { data } = await api.post(`/freight-requests/${r.id}/complete`);
      toast.success(data.status === "completed" ? "Marked as completed" : "Reopened");
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const suggest = async (qid) => {
    setBusy(qid);
    try {
      const { data } = await api.post(`/freight-requests/${r.id}/suggest`, { quote_id: qid });
      toast.success(data.suggested_quote_id ? "Marked as your suggested option" : "Suggestion cleared");
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const handoff = async () => {
    setBusy("handoff");
    try {
      await api.post(`/freight-requests/${r.id}/handoff`);
      toast.success("Sent back to the Operations Desk");
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const nQ = quotes.length;
  const hasWinner = !!r.selected_quote_id;
  const isCompleted = r.status === "completed";
  const [view, setView] = useState(() => (!nQ ? 1 : !r.local_costs ? 2 : 3));
  const done = { 1: nQ ? `${nQ} Of 3 Added` : "", 2: r.local_costs ? "Saved" : "", 3: isCompleted ? "Completed" : r.handed_to_ops ? (hasWinner ? "Sent · Winner Selected" : "Sent To Ops") : "" };
  const stepMeta = FREIGHT_STEPS[view - 1];
  return (
    <div className="rounded-xl border border-border bg-white" data-testid={`freight-detail-${r.id}`}>
      <div className="border-b border-border px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-heading text-xl font-bold tracking-tight text-slate-900 sm:text-2xl" data-testid={`freight-supplier-${r.id}`}>{r.supplier_name}</span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{r.rfq_code}</span>
            </div>
            <div className="mt-1 text-base font-bold text-slate-800 sm:text-lg" data-testid={`freight-client-${r.id}`}>{r.client_name || "—"}{r.rfq_title ? ` · ${r.rfq_title}` : ""}</div>
            <details className="mt-2 text-sm" data-testid={`freight-addresses-${r.id}`}>
              <summary className="cursor-pointer text-xs font-medium text-[#1B4383]">Addresses &amp; Delivery Point</summary>
              <div className="mt-1.5 space-y-1">
              <div><span className="font-semibold text-slate-600">Supplier Address:</span> {r.supplier_address ? <span className="text-slate-700">{r.supplier_address}</span> : <span className="italic text-muted-foreground">Not set in CRM</span>}</div>
              <div><span className="font-semibold text-slate-600">Client Address:</span> {r.client_address ? <span className="text-slate-700">{r.client_address}</span> : <span className="italic text-muted-foreground">Not set in CRM</span>}</div>
              <div><span className="font-semibold text-slate-600">Client&apos;s Preferred Delivery Point:</span> {r.preferred_delivery_point ? <span className="font-medium text-[#1B4383]">{r.preferred_delivery_point}</span> : <span className="italic text-muted-foreground">Not set in CRM</span>}</div>
              </div>
            </details>
            <Link to={`/rfqs/${r.rfq_id}/quotes/${r.supplier_id}`} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[#1B4383] hover:underline" data-testid={`freight-open-quote-${r.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Open Supplier Quote &amp; Costing
            </Link>
          </div>
          <div className="flex flex-row items-center gap-2">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`} data-testid={`freight-status-${r.id}`}>{meta.label}</span>
            {canSelect && r.status === "selected" && (
              <Button size="sm" onClick={complete} disabled={busy === "complete"} className="h-7 bg-[#1B4383] hover:bg-[#16386e]" data-testid={`freight-complete-${r.id}`}>
                {busy === "complete" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}Mark Completed
              </Button>
            )}
            {canSelect && r.status === "completed" && (
              <button onClick={complete} disabled={busy === "complete"} className="text-xs text-[#1B4383] hover:underline" data-testid={`freight-reopen-${r.id}`}>Reopen</button>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <span>Mode <b className="uppercase text-slate-600" data-testid={`freight-modes-${r.id}`}>{r.mode === "delivery" ? "In-country delivery" : (r.methods || [r.method]).join(" + ")}</b></span>
          {r.rfq_created_at && <span>RFQ Added <b className="text-slate-600" data-testid={`freight-rfq-added-${r.id}`}>{fmtDay(r.rfq_created_at)}</b></span>}
          {r.created_at && <span>Freight Requested <b className="text-slate-600">{fmtDay(r.created_at)}</b></span>}
          <span>Shipment <b className="capitalize text-slate-600">{r.shipment_type}</b></span>
          {r.chargeable_kg != null && <span>Chargeable <b className="font-mono text-slate-600">{r.chargeable_kg} kg</b></span>}
          {!compact && <span className={quotes.length >= 3 ? "text-[#527D63]" : "text-[#A87F31]"}><b>{quotes.length}/3</b> quotes</span>}
        </div>
      </div>
      <div className="px-5 py-4 space-y-5">
        {compact ? (multi &&
          <ModeTabs r={r} active={activeMode} setActive={(m) => { setActiveMode(m); setShowAdd(false); setEditId(null); }} />
        ) : (<>
        <FreightStepper r={r} view={view} setView={setView} done={done} />
        <div data-testid={`freight-guidance-${r.id}`}>
          <div className="text-[10px] font-semibold tracking-wide text-[#1B4383]">Step {view} Of 3</div>
          <div className="font-heading text-base font-bold text-slate-900">{stepMeta.label}</div>
          <p className="text-xs text-muted-foreground">{stepMeta.desc}</p>
        </div>
        </>)}

        {r.reopened_by_ops && (
          <div className="flex items-start gap-2 rounded-lg border border-[#E7D3A1] bg-[#FBF6E9] px-3.5 py-2.5 text-xs text-[#8A6D1F]" data-testid={`freight-reopened-${r.id}`}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span><b>Reopened by Operations.</b> The freight details were updated{r.reopened_by_ops_name ? ` by ${r.reopened_by_ops_name}` : ""}. Review the changes, update your forwarder quotes, then send them back to the Operations desk.</span>
          </div>
        )}

        {(multi || view === 1) && (r.items || []).length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer text-[#1B4383]"><Package className="mr-1 inline h-3.5 w-3.5" />{r.items.length} Line Item{r.items.length !== 1 ? "s" : ""}</summary>
            <ul className="mt-1 space-y-0.5 pl-5">
              {r.items.map((it, i) => <li key={i} className="list-disc">{it.description || "(item)"} — qty {it.qty ?? "—"}{it.weight_kg ? `, ${it.weight_kg}kg` : ""}</li>)}
            </ul>
          </details>
        )}

        {/* Step 1 — collect quotes (form folded until needed) */}
        {!compact && view === 1 && canQuote && (showAdd || shownQuotes.length === 0) ? (
          <div id={`fq-add-${r.id}`}>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-heading text-sm font-bold text-slate-900">Add {multi ? `A ${MODE_NAME[activeMode]}` : "A Forwarder"} Quote</h4>
              {shownQuotes.length > 0 && <button type="button" onClick={() => setShowAdd(false)} className="text-xs text-muted-foreground hover:text-slate-700" data-testid={`fq-add-close-${r.id}`}>Close</button>}
            </div>
            <QuoteForm key={activeMode} reqId={r.id} onAdded={() => { setShowAdd(false); onChanged(); }} mode={r.mode} methods={multi ? [activeMode] : r.methods || []} />
          </div>
        ) : null}

        {!compact && view === 1 && (
        <div className="min-w-0 overflow-x-auto">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-heading text-sm font-bold text-slate-900">{multi ? `${MODE_NAME[activeMode]} Rates` : "Forwarder Quotes"} <span className={`ml-1 text-xs font-semibold ${shownQuotes.length >= 3 ? "text-[#527D63]" : "text-[#A87F31]"}`} data-testid={`freight-progress-${r.id}`}>{shownQuotes.length} of 3</span></h4>
            {canQuote && shownQuotes.length > 0 && !showAdd && <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid={`fq-add-open-${r.id}`}><Plus className="mr-1.5 h-4 w-4" />Add {multi ? MODE_NAME[activeMode] : "Forwarder"} Quote</Button>}
          </div>
          {!canQuote && <p className="mb-2 text-xs text-muted-foreground">You don't have permission to add quotes.</p>}
          {shownQuotes.length > 0 ? (
            <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[520px] text-xs" data-testid={`freight-quotes-${r.id}`}>
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-1.5 pr-3 font-medium">Forwarder</th>
                  <th className="py-1.5 px-4 font-medium text-right">Amount</th>
                  <th className="hidden py-1.5 px-3 font-medium sm:table-cell">Transit Time</th>
                  <th className="hidden py-1.5 px-3 font-medium sm:table-cell">Notes</th>
                  <th className="py-1.5 px-3 font-medium">Quote</th>
                  <th className="py-1.5 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {shownQuotes.map((q) => {
                  const isSel = r.selected_quote_id === q.id;
                  const isSuggested = r.suggested_quote_id === q.id;
                  return (
                    <React.Fragment key={q.id}>
                    <tr className={`border-t border-border ${isSel ? "bg-[#F3F8F4]" : ""}`} data-testid={`freight-quote-${q.id}`}>
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{isSel && <Crown className="mr-1 inline h-3.5 w-3.5 text-[#527D63]" />}{q.forwarder_name}{q.method && ((r.methods || []).length > 1 || !(r.methods || [r.method]).includes(q.method)) && <span className="ml-1.5 rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#1B4383]" data-testid={`quote-mode-${q.id}`}>{q.method}</span>}{isSuggested && <span className="ml-1.5 rounded-full bg-[#F6EDD9] px-1.5 py-0.5 text-[10px] font-medium text-[#A87F31]">suggested</span>}</td>
                      <td className="py-1.5 px-4 text-right font-mono whitespace-nowrap">{money(q.currency, q.amount)}{q.amount === Math.min(...quotes.filter((x) => (x.method || "") === (q.method || "")).map((x) => x.amount)) && quotes.filter((x) => (x.method || "") === (q.method || "")).length > 1 && <span className="ml-1.5 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] text-[#527D63]">lowest</span>}</td>
                      <td className="hidden py-1.5 px-3 text-muted-foreground whitespace-nowrap sm:table-cell">{fmtTransit(q)}</td>
                      <td className="hidden py-1.5 px-3 text-muted-foreground sm:table-cell">{q.notes || "—"}</td>
                      <td className="py-1.5 px-3">
                        {q.file_id ? (
                          <button onClick={() => viewFile(q.file_id)} className="inline-flex items-center gap-1 text-[#1B4383] hover:underline" data-testid={`view-quote-${q.id}`}><Eye className="h-3.5 w-3.5" /> View</button>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-1.5 pl-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {canQuote && (
                            <button onClick={() => suggest(q.id)} disabled={busy === q.id} title={isSuggested ? "Your suggested option — click to clear" : "Mark as your suggested option"} className={`p-1 transition-colors ${isSuggested ? "text-[#A87F31]" : "text-slate-300 hover:text-[#A87F31]"}`} data-testid={`suggest-quote-${q.id}`}>
                              <Star className={`h-3.5 w-3.5 ${isSuggested ? "fill-[#A87F31]" : ""}`} />
                            </button>
                          )}
                          {canSelect && (
                            <Button size="sm" variant={isSel ? "default" : "outline"} onClick={() => select(q.id)} disabled={busy === q.id} data-testid={`select-quote-${q.id}`}>
                              {busy === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isSel ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : null}{isSel ? "Winner" : "Select"}
                            </Button>
                          )}
                          {canQuote && <button onClick={() => startEdit(q)} className="text-slate-400 hover:text-[#1B4383]" data-testid={`edit-quote-${q.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                          {canQuote && <button onClick={() => remove(q.id)} className="text-slate-400 hover:text-destructive" data-testid={`remove-quote-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                        </div>
                      </td>
                    </tr>
                    {editId === q.id && (
                      <tr className="border-t border-border bg-[#FFFBEB]" data-testid={`freight-quote-edit-${q.id}`}>
                        <td colSpan={6} className="p-3">
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                            <div><Label className="text-[11px]">Currency</Label><Input value={editF.currency} onChange={(e) => setEditF({ ...editF, currency: e.target.value })} className="h-9" data-testid={`edit-curr-${q.id}`} /></div>
                            <div><Label className="text-[11px]">Amount</Label><Input inputMode="decimal" value={fmtThousands(editF.amount)} onChange={(e) => setEditF({ ...editF, amount: e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "") })} className="h-9" data-testid={`edit-amount-${q.id}`} /></div>
                            <div>
                              <div className="flex items-center justify-between gap-1">
                                <Label className="text-[11px]">Transit Time</Label>
                                <div className="inline-flex overflow-hidden rounded-md border border-border">
                                  {[["weeks", "Wks"], ["days", "Days"]].map(([v, l]) => (
                                    <button key={v} type="button" onClick={() => setEditUnit(v)} className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${editUnit === v ? "bg-[#12294B] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} data-testid={`edit-transit-unit-${v}-${q.id}`}>{l}</button>
                                  ))}
                                </div>
                              </div>
                              <Input type="number" value={editF.lead_weeks} onChange={(e) => setEditF({ ...editF, lead_weeks: e.target.value })} placeholder={editUnit === "days" ? "e.g. 7" : "e.g. 1"} className="h-9" data-testid={`edit-transit-${q.id}`} />
                            </div>
                            <div className="col-span-2"><Label className="text-[11px]">Notes</Label><Input value={editF.notes} onChange={(e) => setEditF({ ...editF, notes: e.target.value })} placeholder="Optional" className="h-9" data-testid={`edit-notes-${q.id}`} /></div>
                          </div>
                          <div className="mt-2 flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => setEditId(null)} data-testid={`edit-cancel-${q.id}`}>Cancel</Button>
                            <Button size="sm" onClick={() => saveEdit(q.id)} disabled={busy === q.id} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid={`edit-save-${q.id}`}>{busy === q.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Save</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border py-6 text-center text-xs text-muted-foreground">No quotes yet — add your first one above.</div>
          )}
        </div>
        )}
        {!compact && view === 1 && <StepNav r={r} onNext={() => setView(2)} nextDisabled={!nQ} nextLabel="Next: Transport & Misc" hint={!nQ ? "Add At Least 1 Quote First" : nQ < 3 ? `${3 - nQ} More Recommended` : ""} />}

        {compact && <ModePanel r={r} m={multi ? activeMode : null} canQuote={canQuote} canSelect={canSelect} busy={busy} onChanged={onChanged} onSaved={multi ? nextMode : undefined} onRemove={remove} onSuggest={suggest} onView={viewFile} onSelect={select} />}
        {compact && <MultiSend r={r} canQuote={canQuote} busy={busy} onHandoff={handoff} />}
        {!compact && view === 2 && <LocalCostsCard key={`${r.id}-${r.local_costs?.at || ""}`} r={r} canQuote={canQuote} onChanged={onChanged} onSaved={() => setView(3)} />}
        {!compact && view === 2 && <StepNav r={r} onBack={() => setView(1)} onNext={() => setView(3)} nextDisabled={!r.local_costs} nextLabel="Next: Review & Send" />}

        {!compact && view === 3 && <ReviewStep r={r} quotes={quotes} canQuote={canQuote} busy={busy} onSuggest={suggest} onHandoff={handoff} />}
        {!compact && view === 3 && <StepNav r={r} onBack={() => setView(2)} />}

        <details>
          <summary className="cursor-pointer font-heading text-sm font-bold text-slate-900" data-testid={`freight-attachments-toggle-${r.id}`}>Attachments</summary>
          <div className="mt-2"><FreightFiles rfqId={r.rfq_id} supplierId={r.supplier_id} canQuote={canQuote} /></div>
        </details>

        {(r.activity || []).length > 0 && (
          <details>
            <summary className="cursor-pointer font-heading text-sm font-bold text-slate-900" data-testid={`freight-activity-toggle-${r.id}`}>Activity ({(r.activity || []).length})</summary>
          <div className="mt-2">
            <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Select value={actFilter.action} onValueChange={(v) => setActFilter((f) => ({ ...f, action: v }))}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`act-filter-action-${r.id}`}><SelectValue placeholder="All actions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {actOptions.map((a) => <SelectItem key={a} value={a}>{ACT_LABEL[a] || a}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" className="h-8 w-[140px] text-xs" value={actFilter.from} max={actFilter.to || undefined} onChange={(e) => setActFilter((f) => ({ ...f, from: e.target.value }))} data-testid={`act-filter-from-${r.id}`} />
                <span className="text-[11px] text-muted-foreground">to</span>
                <Input type="date" className="h-8 w-[140px] text-xs" value={actFilter.to} min={actFilter.from || undefined} onChange={(e) => setActFilter((f) => ({ ...f, to: e.target.value }))} data-testid={`act-filter-to-${r.id}`} />
                {actDirty && <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setActFilter({ action: "all", from: "", to: "" })} data-testid={`act-filter-clear-${r.id}`}>Clear</Button>}
              </div>
            </div>
            {shownActs.length ? (
              <ol className="space-y-2 border-l border-border pl-4" data-testid={`freight-activity-${r.id}`}>
                {shownActs.map((a) => (
                  <li key={a.id} className="relative text-xs" data-testid={`freight-activity-item-${a.id}`}>
                    <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-[#1B4383] ring-2 ring-white" />
                    <div className="text-slate-700">{a.summary}</div>
                    <div className="text-[10px] text-muted-foreground">{a.actor_name}{a.at ? ` · ${new Date(a.at).toLocaleString()}` : ""}</div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-md border border-dashed border-border py-6 text-center text-xs text-muted-foreground" data-testid={`freight-activity-empty-${r.id}`}>No activity matches this filter.</p>
            )}
          </div>
          </details>
        )}
      </div>
    </div>
  );
}

const SHIP_STAGES = [
  { key: "booked", label: "Booked" },
  { key: "supplier_collected", label: "Supplier Collected" },
  { key: "departed", label: "Departed (POL)" },
  { key: "arrived", label: "Arrived (POD)" },
  { key: "customs", label: "Customs Clearance" },
  { key: "in_transit_local", label: "In Transit (Local)" },
  { key: "delivered", label: "Delivered" },
];
const SHIP_STAGE_LABEL = Object.fromEntries(SHIP_STAGES.map((s) => [s.key, s.label]));
const HEALTH_META = {
  on_track: { label: "On Track", cls: "bg-[#E4EEE8] text-[#527D63]" },
  at_risk: { label: "At Risk", cls: "bg-[#F4E7E7] text-[#9D4848]" },
  delivered: { label: "Delivered", cls: "bg-[#E6ECF5] text-[#1B4383]" },
};
const SHIP_PRIORITY = { low: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };
const CAT_TINT = { local: "bg-sky-100 text-sky-700", international: "bg-violet-100 text-violet-700" };
const MODE_ICON = { air: Plane, road: Truck, sea: Ship };
const emptyShipment = { order_id: "", supplier_name: "", carrier: "", mode: "sea", tracking_no: "", pol: "", pod: "", etd: "", eta: "", stage: "booked", priority: "medium", notes: "" };

function ShipmentDialog({ open, onOpenChange, initial, orders, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState(emptyShipment);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(initial?.id ? { ...emptyShipment, ...initial, order_id: initial.order_id || "" } : { ...emptyShipment });
  }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, order_id: f.order_id === "__none__" ? "" : f.order_id };
      if (editing) await api.put(`/shipments/${initial.id}`, payload);
      else await api.post(`/shipments`, payload);
      toast.success(editing ? "Shipment updated" : "Shipment created");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="shipment-dialog">
        <DialogHeader><DialogTitle>{editing ? `Edit ${initial.shipment_no}` : "New Shipment"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-[11px]">Linked Order</Label>
            <Select value={f.order_id || "__none__"} onValueChange={(v) => set("order_id", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="ship-order-select"><SelectValue placeholder="Link to an order (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No linked order</SelectItem>
                {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_no} · {o.client_name || "—"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px]">Carrier / Forwarder</Label><Input value={f.carrier} onChange={(e) => set("carrier", e.target.value)} className="h-9" data-testid="ship-carrier" /></div>
          <div><Label className="text-[11px]">Supplier</Label><Input value={f.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} className="h-9" data-testid="ship-supplier" /></div>
          <div>
            <Label className="text-[11px]">Mode</Label>
            <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="ship-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="air">Air</SelectItem><SelectItem value="road">Road</SelectItem><SelectItem value="sea">Sea</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px]">Tracking / BL / AWB #</Label><Input value={f.tracking_no} onChange={(e) => set("tracking_no", e.target.value)} className="h-9" data-testid="ship-tracking" /></div>
          <div><Label className="text-[11px]">Origin (POL)</Label><Input value={f.pol} onChange={(e) => set("pol", e.target.value)} placeholder="e.g. Johannesburg" className="h-9" data-testid="ship-pol" /></div>
          <div><Label className="text-[11px]">Destination (POD)</Label><Input value={f.pod} onChange={(e) => set("pod", e.target.value)} placeholder="e.g. Dar es Salaam" className="h-9" data-testid="ship-pod" /></div>
          <div><Label className="text-[11px]">ETD (departure)</Label><Input type="date" value={f.etd || ""} onChange={(e) => set("etd", e.target.value)} className="h-9" data-testid="ship-etd" /></div>
          <div><Label className="text-[11px]">ETA (arrival)</Label><Input type="date" value={f.eta || ""} onChange={(e) => set("eta", e.target.value)} className="h-9" data-testid="ship-eta" /></div>
          <div>
            <Label className="text-[11px]">Stage</Label>
            <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="ship-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{SHIP_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Priority</Label>
            <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="ship-priority"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="text-[11px]">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="ship-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ship-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="ship-save" className="bg-[#1B4383] hover:bg-[#16386e]">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}{editing ? "Save changes" : "Create shipment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShipmentCard({ s, onChanged, onEdit }) {
  const nav = useNavigate();
  const Icon = MODE_ICON[s.mode] || Ship;
  const hm = HEALTH_META[s.health] || HEALTH_META.on_track;
  const remove = async () => {
    if (!window.confirm(`Delete shipment ${s.shipment_no}?`)) return;
    try { await api.delete(`/shipments/${s.id}`); toast.success("Shipment deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <MobileCard testid={`ship-card-${s.id}`} onOpen={() => nav(`/logistics/shipment/${s.id}`)}
      title={s.shipment_no} subtitle={s.order_no || "No linked order"}
      badge={<span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${hm.cls}`}>{s.health === "at_risk" && <AlertTriangle className="h-3 w-3" />}{hm.label}</span>}
      body={<>{s.client_name || "—"}{s.rfq_title && <div className="text-xs text-muted-foreground line-clamp-2">{s.rfq_title}</div>}</>}
      fields={[["Supplier", s.supplier_name || "—"], ["Stage", SHIP_STAGE_LABEL[s.stage] || s.stage],
        ["Route", <span key="r" className="inline-flex items-center gap-1"><Icon className="h-3 w-3 text-slate-400" />{s.mode?.toUpperCase()} · {s.pol || "—"} → {s.pod || "—"}</span>],
        ["ETA", <span key="e" className={s.health === "at_risk" ? "font-semibold text-[#9D4848]" : ""}>{s.eta ? fmtDate(s.eta) : "—"}</span>]]}
      actions={<>
        <button onClick={() => onEdit(s)} className="rounded-md border border-input p-2 text-slate-500 hover:bg-slate-50" data-testid={`ship-card-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={remove} className="rounded-md border border-input p-2 text-slate-400 hover:text-destructive" data-testid={`ship-card-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
      </>} />
  );
}

function ShipmentRow({ s, onChanged, onEdit, highlight }) {
  const nav = useNavigate();
  const Icon = MODE_ICON[s.mode] || Ship;
  const hm = HEALTH_META[s.health] || HEALTH_META.on_track;
  const rowRef = useRef(null);
  useEffect(() => { if (highlight && rowRef.current) rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [highlight]);
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete shipment ${s.shipment_no}?`)) return;
    try { await api.delete(`/shipments/${s.id}`); toast.success("Shipment deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <tr ref={rowRef} onClick={() => nav(`/logistics/shipment/${s.id}`)} className={`cursor-pointer border-b border-border last:border-0 hover:bg-slate-50/60 ${highlight ? "bg-[#EEF3FA]" : ""}`} data-testid={`ship-open-${s.id}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-[#1B4383]" data-testid={`ship-no-${s.id}`}>{s.shipment_no}</div>
        <div className="text-[11px] text-muted-foreground">{s.order_no || "No linked order"}</div>
      </td>
      <td className="px-4 py-3 text-slate-800">
        <div>{s.client_name || "—"}</div>
        {s.rfq_title && <div className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2 max-w-[260px]" data-testid={`ship-rfq-title-${s.id}`}>{s.rfq_title}</div>}
      </td>
      <td className="px-4 py-3 text-slate-700">{s.supplier_name || "—"}</td>
      <td className="px-4 py-3">{s.category ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${CAT_TINT[s.category] || "bg-slate-100 text-slate-500"}`} data-testid={`ship-cat-${s.id}`}>{s.category}</span> : <span className="text-slate-300">—</span>}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-slate-700"><Icon className="h-3.5 w-3.5 text-slate-400" /><span className="text-[11px] font-semibold uppercase">{s.mode}</span></span>
        <div className="text-[11px] text-muted-foreground">{s.pol || "—"} → {s.pod || "—"}</div>
      </td>
      <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">{SHIP_STAGE_LABEL[s.stage] || s.stage}</span></td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${hm.cls}`} data-testid={`ship-health-${s.id}`}>{s.health === "at_risk" && <AlertTriangle className="h-3 w-3" />}{hm.label}</span>
        <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${SHIP_PRIORITY[s.priority]}`}>{s.priority ? s.priority.charAt(0).toUpperCase() + s.priority.slice(1) : ""}</span>
      </td>
      <td className="px-4 py-3 text-slate-700">
        <span className={s.health === "at_risk" ? "font-semibold text-[#9D4848]" : ""}>{s.eta ? fmtDate(s.eta) : "—"}</span>
        {s.tracking_no ? <div className="text-[11px] font-mono text-muted-foreground">{s.tracking_no}</div> : null}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={() => onEdit(s)} className="rounded-md border border-input p-1.5 text-slate-500 hover:bg-slate-50" data-testid={`ship-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={remove} className="rounded-md border border-input p-1.5 text-slate-400 hover:text-destructive" data-testid={`ship-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

function ShipmentsPanel({ focusId }) {
  const [ships, setShips] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("active");
  const [filters, setFilters] = useState({ search: "", mode: "all", health: "all" });
  const [shipQ, setShipQ] = useState("");
  const [page, setPage] = useState(1);
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const load = useCallback(() => {
    setLoading(true);
    api.get("/shipments").then(({ data }) => setShips(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}); }, [load]);
  const openNew = () => { setEditing(null); setDlgOpen(true); };
  const openEdit = (s) => { setEditing(s); setDlgOpen(true); };
  const [exporting, setExporting] = useState(false);
  const exportShips = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/shipments/export?view=${view}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `somcore_shipments_${view}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success("Shipments exported");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const total = ships.length;
  const active = ships.filter((s) => s.health !== "delivered").length;
  const atRisk = ships.filter((s) => s.health === "at_risk").length;
  const delivered = ships.filter((s) => s.health === "delivered").length;
  const KPIS = [
    { key: "total", label: "Total Shipments", icon: Package, tint: "text-[#1B4383] bg-[#E6ECF5]", value: `${total}`, sub: "All Shipments" },
    { key: "in-progress", label: "In Progress", icon: Truck, tint: "text-emerald-600 bg-emerald-50", value: `${active}`, sub: "Moving Now" },
    { key: "at-risk", label: "At Risk", icon: AlertTriangle, tint: "text-[#9D4848] bg-[#F4E7E7]", value: `${atRisk}`, sub: "Need Attention" },
    { key: "delivered", label: "Delivered", icon: CheckCircle2, tint: "text-sky-600 bg-sky-50", value: `${delivered}`, sub: "Completed" },
  ];

  const shown = React.useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return ships
      .filter((s) => {
        const isDelivered = s.health === "delivered";
        if (view === "completed" ? !isDelivered : isDelivered) return false;
        if (filters.mode !== "all" && s.mode !== filters.mode) return false;
        if (filters.health !== "all" && s.health !== filters.health) return false;
        if (q) {
          const hay = `${s.shipment_no} ${s.order_no} ${s.client_name} ${s.carrier} ${s.supplier_name} ${s.tracking_no} ${s.pol} ${s.pod}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
  }, [ships, view, filters]);

  const filtersDirty = filters.search || filters.mode !== "all" || filters.health !== "all";

  useEffect(() => { setPage(1); }, [filters, view]);
  const pagedShips = React.useMemo(() => paginate(shown, page), [shown, page]);

  return (
    <div data-testid="shipments-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { load(); api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}); toast.success("Shipments Refreshed"); }} disabled={loading} data-testid="shipments-refresh-btn">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={exportShips} disabled={exporting || !shown.length} data-testid="export-shipments-btn">
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Export
          </Button>
          <Button size="sm" onClick={openNew} data-testid="new-shipment-btn" className="bg-[#1B4383] hover:bg-[#16386e]"><Plus className="mr-1.5 h-4 w-4" />New Shipment</Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="shipments-kpis">
        {KPIS.map((k) => (
          <div key={k.key} className="rounded-lg border border-border bg-white p-4" data-testid={`ship-kpi-${k.key}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold tracking-wide text-slate-400">{k.label}</span>
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${k.tint}`}><k.icon className="h-4 w-4" /></span>
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900">{k.value}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-border bg-white p-1" data-testid="shipments-view-toggle">
        <button onClick={() => setView("active")} data-testid="ship-view-active" className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "active" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>Active ({active})</button>
        <button onClick={() => setView("completed")} data-testid="ship-view-completed" className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "completed" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}><CheckCircle2 className="h-4 w-4" />Delivered ({delivered})</button>
      </div>

      <div className="mb-4 rounded-lg border border-border bg-white p-4" data-testid="shipments-filters">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setF("search", shipQ.trim()); }}>
              <Input className="pl-9" placeholder="Search shipment #, order, client, carrier or tracking…" value={shipQ} onChange={(e) => { setShipQ(e.target.value); if (!e.target.value) setF("search", ""); }} data-testid="ship-search-input" />
              <Button type="submit" className="shrink-0 bg-[#1B4383] hover:bg-[#16386e]" data-testid="ship-search-enter">Enter</Button>
            </form>
          </div>
          <Select value={filters.mode} onValueChange={(v) => setF("mode", v)}>
            <SelectTrigger data-testid="ship-filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All modes</SelectItem><SelectItem value="sea">Sea</SelectItem><SelectItem value="air">Air</SelectItem><SelectItem value="road">Road</SelectItem></SelectContent>
          </Select>
          <Select value={filters.health} onValueChange={(v) => setF("health", v)}>
            <SelectTrigger data-testid="ship-filter-health"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="on_track">On Track</SelectItem><SelectItem value="at_risk">At Risk</SelectItem><SelectItem value="delivered">Delivered</SelectItem></SelectContent>
          </Select>
        </div>
        {filtersDirty && (
          <div className="mt-3 border-t border-border pt-3">
            <Button variant="ghost" size="sm" onClick={() => { setShipQ(""); setFilters({ search: "", mode: "all", health: "all" }); }} data-testid="ship-filter-clear">Clear filters</Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !ships.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center" data-testid="shipments-empty">
          <span className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EAF1FB] text-[#1B4383]"><Package className="h-6 w-6" /></span>
          <div className="font-heading text-base font-bold text-slate-900">No shipments yet</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Create a shipment to track an import or local delivery from booking through to delivery — or open an order and click <b>Track Shipment</b>.</p>
          <Button size="sm" onClick={openNew} className="mt-4 bg-[#1B4383] hover:bg-[#16386e]" data-testid="new-shipment-empty-btn"><Plus className="mr-1.5 h-4 w-4" />New Shipment</Button>
        </div>
      ) : !shown.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="shipments-no-match">No shipments match your filters.</div>
      ) : (
        <>
        <div className="space-y-2.5 sm:hidden" data-testid="shipments-cards">
          {pagedShips.map((s) => <ShipmentCard key={s.id} s={s} onChanged={load} onEdit={openEdit} />)}
        </div>
        <div className="hidden overflow-x-auto rounded-xl border border-border bg-white sm:block" data-testid="shipments-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">Shipment No</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Supplier</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Route</th>
                <th className="px-4 py-3 font-medium">Stage</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">ETA</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedShips.map((s) => <ShipmentRow key={s.id} s={s} onChanged={load} onEdit={openEdit} highlight={s.id === focusId} />)}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && shown.length > 0 && <PaginationBar page={page} setPage={setPage} total={shown.length} testid="shipments-pagination" />}
      <ShipmentDialog open={dlgOpen} onOpenChange={setDlgOpen} initial={editing} orders={orders} onSaved={load} />
    </div>
  );
}

export default function LogisticsDashboard() {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [freightType, setFreightType] = useState("all");
  const [freightView, setFreightView] = useState("active");
  const [fq, setFq] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSupplier, setFSupplier] = useState("all");
  const [fSort, setFSort] = useState("desc");
  const [reviews, setReviews] = useState([]);
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section") === "shipments" ? "shipments" : "freight";
  useEffect(() => { api.get("/logistics/reviews").then(({ data }) => setReviews(data)).catch(() => {}); }, []);
  const role = user?.role || "staff";
  const canQuote = ["logistics", "admin", "staff"].includes(role);
  const canSelect = ["logistics", "admin", "staff"].includes(role);

  const load = useCallback(() => {
    api.get("/freight-requests").then(({ data }) => setReqs(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sortedReqs = [...reqs].sort((a, b) => (fSort === "asc" ? 1 : -1) * String(a.created_at || "").localeCompare(String(b.created_at || "")));
  const matchFilter = (r) =>
    (freightView === "completed" ? r.status === "completed" : r.status !== "completed") &&
    (freightType === "all" || (freightType === "local" ? r.mode === "delivery" : r.mode !== "delivery")) &&
    (fStatus === "all" || r.status === fStatus) &&
    (fSupplier === "all" || r.supplier_name === fSupplier) &&
    (!fSearch || `${r.supplier_name} ${r.client_name} ${r.rfq_code} ${r.rfq_title}`.toLowerCase().includes(fSearch.toLowerCase()));
  const visibleReqs = sortedReqs.filter(matchFilter);
  useEffect(() => {
    if (!visibleReqs.length) { setSelectedId(null); return; }
    setSelectedId((cur) => visibleReqs.some((r) => r.id === cur) ? cur : visibleReqs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqs, freightType, freightView, fStatus, fSearch, fSupplier, fSort]);
  const selected = reqs.find((r) => r.id === selectedId) || null;
  const freightStats = [
    { label: "Awaiting Input", value: reqs.filter((r) => r.status === "awaiting_input").length, tone: "text-slate-900" },
    { label: "Quotes In", value: reqs.filter((r) => r.status === "quoted").length, tone: "text-slate-900" },
    { label: "Selected", value: reqs.filter((r) => r.status === "selected").length, tone: "text-slate-900" },
    { label: "Completed", value: reqs.filter((r) => r.status === "completed").length, tone: "text-slate-900" },
  ];

  return (
    <AppLayout title="Logistics">
      {section === "freight" && (<>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : !reqs.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground" data-testid="freight-empty">
          No freight requests yet. They appear here when a buyer clicks <b>Request freight quotes</b> on an international supplier's costing.
        </div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="freight-stats">
            {freightStats.map((s) => (
              <div key={s.label} className="rounded-xl border border-border bg-white px-4 py-3">
                <div className={`font-heading text-2xl font-bold ${s.tone}`}>{s.value}</div>
                <div className="text-[11px] font-medium tracking-wide text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mb-3 inline-flex rounded-lg border border-border bg-white p-1">
            {[["active", "Active"], ["completed", "Completed"]].map(([k, l]) => (
              <FilterChip key={k} active={freightView === k} onClick={() => { setFreightView(k); setFStatus("all"); }} testid={`freight-view-${k}`}>{k === "completed" && <CheckCircle2 className="h-4 w-4" />}{l} ({reqs.filter((r) => (k === "completed") === (r.status === "completed")).length})</FilterChip>
            ))}
          </div>
          <div className="mb-4 space-y-3 rounded-lg border border-border bg-white p-4" data-testid="freight-filters">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
              <form className="relative flex gap-2 md:col-span-3" onSubmit={(e) => { e.preventDefault(); setFSearch(fq.trim()); }}>
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" placeholder="Search Supplier, Client Or SOM-REQ…" value={fq} onChange={(e) => { setFq(e.target.value); if (!e.target.value) setFSearch(""); }} data-testid="freight-search-input" />
                <Button type="submit" className="shrink-0 bg-[#1B4383] hover:bg-[#16386e]" data-testid="freight-search-enter">Enter</Button>
              </form>
              <Select value={freightType} onValueChange={setFreightType}>
                <SelectTrigger data-testid="freight-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="international">International</SelectItem><SelectItem value="local">Local</SelectItem></SelectContent>
              </Select>
              <Select value={fStatus} onValueChange={setFStatus} disabled={freightView === "completed"}>
                <SelectTrigger data-testid="freight-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="awaiting_input">Awaiting Input</SelectItem>
                  <SelectItem value="quoted">Quotes In</SelectItem>
                  <SelectItem value="selected">Selected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fSupplier} onValueChange={setFSupplier}>
                <SelectTrigger data-testid="freight-filter-supplier"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Suppliers</SelectItem>{[...new Set(reqs.map((r) => r.supplier_name).filter(Boolean))].sort().map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 text-sm text-slate-600">
              <span>Sort By Request Date</span>
              <Select value={fSort} onValueChange={setFSort}>
                <SelectTrigger className="h-9 w-[170px]" data-testid="freight-sort"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="desc">Newest First</SelectItem><SelectItem value="asc">Oldest First</SelectItem></SelectContent>
              </Select>
              {(fSearch || fq || freightType !== "all" || fStatus !== "all" || fSupplier !== "all") && <Button variant="ghost" size="sm" onClick={() => { setFq(""); setFSearch(""); setFreightType("all"); setFStatus("all"); setFSupplier("all"); }} data-testid="freight-filter-clear">Clear Filters</Button>}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
            <div className="lg:max-h-[72vh] lg:overflow-y-auto lg:pr-1" data-testid="freight-list">
              <div className="space-y-2">
                {visibleReqs.length
                  ? visibleReqs.map((r) => <RequestRow key={r.id} r={r} active={r.id === selectedId} onClick={() => setSelectedId(r.id)} />)
                  : <div className="rounded-lg border border-dashed border-border py-10 text-center text-xs text-muted-foreground">No {freightView} {freightType !== "all" ? freightType : ""} requests.</div>}
              </div>
            </div>
            <div data-testid="freight-detail-panel">
              {selected
                ? <RequestDetail key={selected.id} r={selected} canQuote={canQuote} canSelect={canSelect} onChanged={load} />
                : <div className="rounded-xl border border-dashed border-border bg-white py-20 text-center text-sm text-muted-foreground">Select a request on the left to view and enter forwarder quotes.</div>}
            </div>
          </div>
        </>
      )}
      </>)}

      {section === "shipments" && <ShipmentsPanel focusId={searchParams.get("focus")} />}
    </AppLayout>
  );
}
